/**
 * Sentry event/breadcrumb scrubber.
 *
 * Strips Authorization headers, token-shaped strings, and any field whose
 * name matches the sensitive-key pattern from every Sentry-bound payload.
 * Hooked into `Sentry.init({ beforeSend, beforeBreadcrumb })`.
 *
 * Used by:
 *   - `frontend/src/main.tsx`
 *   - regression test `__tests__/sentry-scrub.test.ts`
 */

const SENSITIVE_KEY_RE =
  /token|password|secret|jwt|authorization|api[_-]?key|refresh|access/i;
// JWT-style three-part token. The first part is the base64 header which is
// usually 16-30 chars; allow 10+ so we catch short headers too.
const TOKEN_LIKE_RE =
  /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
// Long opaque tokens (refresh, API keys).
const OPAQUE_TOKEN_RE = /\b[A-Za-z0-9_-]{32,}\b/g;

const REDACTED = "[redacted]";

export function redactString(value: string): string {
  return value.replace(TOKEN_LIKE_RE, REDACTED).replace(OPAQUE_TOKEN_RE, REDACTED);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function scrub(value: unknown, depth = 0): unknown {
  if (depth > 8) return value; // bail on pathological recursion
  if (value == null) return value;
  if (typeof value === "string") return redactString(value);
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  if (!isPlainObject(value)) return value;

  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value)) {
    if (SENSITIVE_KEY_RE.test(key)) {
      out[key] = REDACTED;
      continue;
    }
    out[key] = scrub(v, depth + 1);
  }
  return out;
}

/**
 * Public entry point for Sentry hooks. Returns the same shape it was given
 * (event or breadcrumb), with sensitive content redacted.
 */
export function scrubSentryEvent<T extends object>(input: T | null): T | null {
  if (input == null) return input;
  return scrub(input) as T;
}
